"use client";

import { type FC, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Flex,
  Icon,
  LinkButton,
  Text,
  transitionDurations,
} from "@huub-web/ui";
import type { ClassNameProp, ChildrenProp } from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import {
  focusVisible,
  focusVisibleOffset,
} from "@huub-web/ui/theme/css/focusVisible.css";

import {
  buttonStyle,
  containerStyle,
  contentStyle,
  iconStyle,
  titleStyle,
} from "./FAQListItem.css";

import type { FAQItemRecord } from "../../types";

export type FAQListItemProps = ClassNameProp &
  ChildrenProp & {
    record: FAQItemRecord;
  };

/**
 * FAQListItem component represents a single item in a FAQ list.
 */
export const FAQListItem: FC<FAQListItemProps> = ({
  children,
  record,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const buttonID = `faq-btn-${record.slug}`;
  const contentID = `faq-content-${record.slug}`;

  return (
    <Flex
      as="li"
      className={mergeClasses(containerStyle, className, focusVisible)}
      direction="column"
    >
      <Flex
        as="button"
        id={buttonID}
        onClick={() => setIsOpen(!isOpen)}
        justify="space-between"
        align="flex-end"
        gap="m"
        className={mergeClasses(buttonStyle, focusVisibleOffset)}
        aria-expanded={isOpen}
        aria-controls={contentID}
      >
        <Text className={titleStyle()}>{record.title}</Text>
        <Icon icon="chevronDown" className={iconStyle({ open: isOpen })} />
      </Flex>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={contentID}
            role="region"
            aria-labelledby={buttonID}
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: {
                opacity: 1,
                height: "auto",
                transform: "translateX(0)",
              },
              collapsed: {
                opacity: 0,
                height: 0,
                transform: "translateX(10px)",
              },
            }}
            transition={{
              duration: transitionDurations.s,
              ease: "easeInOut",
            }}
          >
            <Flex className={contentStyle} direction="column" gap="m">
              <Text markdown color="currentColor50">
                {record.summary}
              </Text>
              {record.onClick && (
                <LinkButton onClick={record.onClick}>Lees verder</LinkButton>
              )}
              {children}
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>
  );
};
